import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { debounceTime, Subject } from 'rxjs';
import MailNotification from '../../../../types/mail-notification/MailNotification';
import MailNotificationType from '../../../../types/mail-notification/MailNotificationType';
import MailStats from '../../../../types/mail-notification/MailStats';
import Page from '../../../../types/shared/Page';
import { EnumLabelPipe } from '../../pipes/enum-label.pipe';
import { FormatDateTime } from '../../pipes/format-date-time';
import { MailNotificationService } from '../../services/mail-notification.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-mail-notifications',
    templateUrl: './mail-notifications.component.html',
    styleUrls: [ './mail-notifications.component.scss' ],
    imports: [
        PageHeaderComponent,
        FormsModule,
        InputText,
        IftaLabelModule,
        IconField,
        InputIcon,
        TableModule,
        FormatDateTime,
        Tag,
        Select,
        EnumLabelPipe,
    ]
})
export class MailNotificationsComponent implements OnInit {
    public search: string = '';
    public selectedType: MailNotificationType | null = null;
    public mailNotifications: MailNotification[] = [];
    public mailStats: MailStats | undefined;
    public totalRecords: number = 0;
    public rows: number = 10;
    public currentPage: number = 0;
    public loading: boolean = false;

    public notificationTypes = [
        { label: 'Bounce', value: 'BOUNCE' },
        { label: 'Complaint', value: 'COMPLAINT' },
        { label: 'Delivery', value: 'DELIVERY' },
    ];

    private searchSubject = new Subject<string>();

    constructor(private mailNotificationService: MailNotificationService,
                private messageService: MessageService) {
    }

    /**
     * Initialize the component.
     */
    public ngOnInit(): void {
        this.loadMailNotifications();
        this.loadMailStats();

        // Debounce search input to reduce the number of requests
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0; // Reset to the first page on new search
            this.loadMailNotifications();
        });
    }

    /**
     * Handles page changes.
     *
     * @param event The pagination event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadMailNotifications();
    }

    /**
     * Handles search input changes.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Handles type filter changes.
     */
    public onTypeFilterChange(): void {
        this.currentPage = 0;
        this.loadMailNotifications();
    }

    /**
     * Determines the severity of the tag based on the notification type.
     *
     * @param type The MailNotificationType.
     * @return The severity string for the p-tag component.
     */
    public getTagSeverity(type: MailNotificationType): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (type) {
            case MailNotificationType.DELIVERY:
                return 'success';
            case MailNotificationType.COMPLAINT:
                return 'warn';
            case MailNotificationType.BOUNCE:
                return 'danger';
            default:
                return 'info';
        }
    }

    /**
     * Loads mail notification statistics from the backend.
     */
    private loadMailStats(): void {
        this.mailNotificationService.getStats()
            .then((stats: MailStats) => {
                this.mailStats = stats;
            }).catch((error) => {
            this.messageService.add({ severity: 'error', summary: 'Error loading stats', detail: error.message });
        });

    }

    /**
     * Loads mail notifications from the backend.
     */
    private loadMailNotifications(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';
        const types = this.selectedType ? [ this.selectedType ] : [];

        this.mailNotificationService
            .getMailNotifications(search, types, this.currentPage, size)
            .then((result: Page<MailNotification>) => {
                this.mailNotifications = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error) => {
                this.messageService.add(
                    { severity: 'error', summary: 'Error loading notifications', detail: error.message });
            })
            .finally(() => {
                this.loading = false;
            });
    }

}
